import React from 'react';
import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hoaDonSearchColumns,
  hopDongSearchColumns,
  khachHangSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  CellFieldV2 as CellField,
  SearchFieldV2 as SearchField,
  type InputTableCellParams
} from '@/components/custom/erp';
import { type BoPhan, DotThanhToan, HopDong, KhachHang, KheUoc, Phi, TaiKhoan, VatTu, VuViec } from '@/types/schemas';
import type { DetailItemRow } from '../hooks/useDetailItemRows';
import { FormMode } from '@/types/form';

type DetailCellParams = InputTableCellParams<DetailItemRow>;

export const getDetailItemColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void,
  ma_ngv?: string,
  dien_giai?: string,
  formMode?: FormMode
) => [
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200,
    renderCell: (params: DetailCellParams) => (
      <CellField
        name='dien_giai'
        type='text'
        value={params.row.dien_giai || dien_giai || ''}
        onValueChange={(newValue: unknown) => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 150,
    renderCell: (params: DetailCellParams) => (
      <SearchField<KhachHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
        searchColumns={khachHangSearchColumns}
        columnDisplay='customer_code'
        dialogTitle='Danh mục đối tượng'
        value={params.row.ma_kh_data?.customer_code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => {
          onCellValueChange(params.row.uuid, 'ma_kh_data', row);
        }}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kh',
      resultField: 'ma_kh_data',
      detailEndpoint: QUERY_KEYS.KHACH_HANG
    }
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 200,
    renderCell: (params: DetailCellParams) => (
      <CellField name='ten_kh' type='text' value={params.row.ma_kh_data?.customer_name || ''} />
    ),
    nonWritable: true
  },
  {
    field: 'du_cn',
    headerName: 'Dư công nợ',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <CellField
        name='du_cn'
        type='number'
        value={params.row.ma_kh_data?.credit_limit || 0}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  ...(ma_ngv === '1'
    ? [
        {
          field: 'id_hd',
          headerName: 'Hóa đơn',
          width: 180,
          renderCell: (params: DetailCellParams) => (
            <SearchField<Record<string, unknown>>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.HOA_DON_THEO_KHACH_HANG}/?ma_kh=${params.row.ma_kh_data?.uuid}&document_type=GIAY_BAO_CO`}
              searchColumns={hoaDonSearchColumns}
              columnDisplay='so_ct'
              dialogTitle='Hóa đơn'
              disabled={!params.row.ma_kh_data?.uuid || formMode === 'view'}
              value={params.row.id_hd_data?.so_ct || ''}
              onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'id_hd_data', row)}
              className='w-full'
            />
          )
        },
        {
          field: 'so_ct0_hd',
          headerName: 'Số hóa đơn',
          width: 120,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='so_ct0_hd'
              type='text'
              value={params.row.id_hd_data?.so_hd || ''}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        {
          field: 'ngay_ct_hd',
          headerName: 'Ngày hóa đơn',
          width: 140,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='ngay_ct_hd'
              type='date'
              value={params.row.id_hd_data?.ngay_ct || ''}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        }
      ]
    : []),
  {
    field: 'tk_co',
    headerName: 'Tài khoản có',
    width: 100,
    renderCell: (params: DetailCellParams) => {
      if (ma_ngv === '1') {
        return (
          <CellField
            name='tk_co'
            type='text'
            value={params.row.id_hd_data?.tk_data?.code || ''}
            disabled={formMode === 'view'}
          />
        );
      }

      return (
        <SearchField<TaiKhoan>
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_co_data?.code}
          disabled={formMode === 'view'}
          onRowSelection={(row: unknown) => {
            onCellValueChange(params.row.uuid, 'tk_co_data', row);
          }}
          className='w-full'
        />
      );
    }
  },
  ...(ma_ngv === '1'
    ? [
        {
          field: 'ma_nt_hd',
          headerName: 'Ngoại tệ',
          width: 100,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='ma_nt_hd'
              type='text'
              value={params.row.id_hd_data?.ngoai_te || ''}
              onValueChange={(newValue: unknown) => onCellValueChange(params.row.uuid, 'ma_nt_hd', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        {
          field: 'ty_gia_hd',
          headerName: 'Tỷ giá hđ',
          width: 100,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='ty_gia_hd'
              type='number'
              value={params.row.id_hd_data?.ty_gia || 1}
              onValueChange={(newValue: unknown) => onCellValueChange(params.row.uuid, 'ty_gia_hd', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        {
          field: 'tien_hd_nt',
          headerName: 'Tiền trên hoá đơn',
          width: 140,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='tien_hd_nt'
              type='number'
              value={params.row.id_hd_data?.tien_tren_hd || 0}
              onValueChange={(newValue: unknown) => onCellValueChange(params.row.uuid, 'tien_hd_nt', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        {
          field: 'da_pb_nt',
          headerName: 'Đã phân bổ',
          width: 100,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='da_pb_nt'
              type='number'
              value={params.row.id_hd_data?.da_thanh_toan ?? params.row.da_pb_nt ?? 0}
            />
          ),
          nonWritable: true
        },
        {
          field: 'cl_nt',
          headerName: 'Còn lại',
          width: 140,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='cl_nt'
              type='number'
              value={params.row.id_hd_data?.tien_con_phai_tt || 0}
              onValueChange={(newValue: unknown) => onCellValueChange(params.row.uuid, 'cl_nt', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        }
      ]
    : []),
  ...(ma_ngv !== '1'
    ? [
        {
          field: 'ten_tk_co',
          headerName: 'Tên tài khoản',
          width: 150,
          renderCell: (params: DetailCellParams) => (
            <CellField
              name='ten_tk_co'
              type='text'
              value={params.row.tk_co_data?.name || ''}
              disabled={formMode === 'view'}
            />
          )
        }
      ]
    : []),
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 130,
    renderCell: (params: DetailCellParams) => (
      <CellField
        name='tien_nt'
        type='number'
        value={params.row.tien_nt || 0}
        onValueChange={(newValue: unknown) => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || params.row.ma_hd || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || params.row.ma_dtt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 200,
    renderCell: (params: DetailCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<Record<string, unknown>>
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='so_ct'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.so_ct || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: (params: DetailCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cpkhl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp_khl || params.row.ma_cp0 || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  }
];
